<template>
  <div class="api-terms-page">
    <div class="hero-section">
      <div class="container">
        <h1>API Terms of Service</h1>
        <p class="subtitle">Last Updated: December 14, 2025</p>
      </div>
    </div>

    <div class="container content">
      <div class="toc">
        <h3>Table of Contents</h3>
        <ul>
          <li><a href="#acceptance">1. Acceptance of Terms</a></li>
          <li><a href="#description">2. Service Description</a></li>
          <li><a href="#api-access">3. API Access & Keys</a></li>
          <li><a href="#rate-limits">4. Rate Limits & Usage</a></li>
          <li><a href="#acceptable-use">5. Acceptable Use Policy</a></li>
          <li><a href="#disclaimers">6. Investment Disclaimers</a></li>
          <li><a href="#data-privacy">7. Data & Privacy</a></li>
          <li><a href="#liability">8. Limitation of Liability</a></li>
          <li><a href="#termination">9. Termination</a></li>
          <li><a href="#changes">10. Changes to Terms</a></li>
        </ul>
      </div>

      <section id="acceptance" class="section">
        <h2>1. Acceptance of Terms</h2>
        <p>
          By accessing or using the InvestBuddy API (the "Service"), you agree to be bound by these API Terms of Service 
          ("Terms"). If you do not agree to these Terms, you may not access or use the Service.
        </p>
        <p>
          These Terms supplement and are in addition to our main <router-link to="/terms">Terms of Service</router-link> 
          and <router-link to="/privacy">Privacy Policy</router-link>.
        </p>
      </section>

      <section id="description" class="section">
        <h2>2. Service Description</h2>
        <p>
          The InvestBuddy API provides programmatic access to AI-powered stock predictions and portfolio analysis. 
          Our service uses a validated LSTM (Long Short-Term Memory) neural network model with <strong>55.41% OOS win rate</strong> 
          in real backtesting (2022-2025, monthly rebalancing, 241 trades on 58 stocks).
        </p>
        <div class="feature-box">
          <h4>Available Endpoints:</h4>
          <ul>
            <li><code>POST /mcp/predict</code> - Get 10-day stock price predictions</li>
            <li><code>GET /mcp/market-regime</code> - Analyze current market conditions</li>
            <li><code>POST /mcp/discover</code> - Discover high-potential stocks</li>
            <li><code>POST /mcp/analyze-portfolio</code> - Get portfolio risk analysis</li>
            <li><code>POST /mcp/batch-predict</code> - Bulk predictions (up to 50 symbols)</li>
          </ul>
        </div>
      </section>

      <section id="api-access" class="section">
        <h2>3. API Access & Authentication</h2>
        
        <h3>3.1 API Keys</h3>
        <p>
          You must authenticate all API requests using a valid API key. API keys are generated through:
        </p>
        <ul>
          <li><strong>Web Dashboard:</strong> Generate keys at <router-link to="/settings/api-keys">Settings → API Keys</router-link></li>
          <li><strong>ChatGPT OAuth:</strong> Automatic key generation when installing the ChatGPT Action</li>
          <li><strong>Claude Desktop:</strong> Manual configuration with generated key</li>
        </ul>

        <h3>3.2 Key Security</h3>
        <div class="warning-box">
          <strong>⚠️ Security Requirements:</strong>
          <ul>
            <li>Keep your API keys confidential and secure</li>
            <li>Never share keys publicly or commit them to version control</li>
            <li>Revoke compromised keys immediately</li>
            <li>Use separate keys for different applications</li>
            <li>Rotate keys periodically for enhanced security</li>
          </ul>
        </div>

        <h3>3.3 Key Management</h3>
        <p>
          You may create up to <strong>10 active API keys</strong> per account. Keys can be revoked or deleted at any time 
          through your dashboard. Revoked keys cannot be reactivated - you must generate a new key.
        </p>
      </section>

      <section id="rate-limits" class="section">
        <h2>4. Rate Limits & Usage Tiers</h2>
        
        <div class="pricing-grid">
          <div class="pricing-card">
            <div class="tier-badge free">FREE</div>
            <h3>Trial / Free Tier</h3>
            <div class="price">$0<span>/month</span></div>
            <ul class="features">
              <li><strong>30 requests per day</strong></li>
              <li>All endpoints available</li>
              <li>Basic rate limiting</li>
              <li>Community support</li>
            </ul>
          </div>

          <div class="pricing-card popular">
            <div class="tier-badge developer">DEVELOPER</div>
            <h3>Developer Tier</h3>
            <div class="price">$29<span>/month</span></div>
            <ul class="features">
              <li><strong>150 requests per day</strong></li>
              <li>All endpoints available</li>
              <li>Priority rate limiting</li>
              <li>Email support</li>
              <li>Usage analytics</li>
            </ul>
          </div>

          <div class="pricing-card">
            <div class="tier-badge business">BUSINESS</div>
            <h3>Business Tier</h3>
            <div class="price">$299<span>/month</span></div>
            <ul class="features">
              <li><strong>1,000 requests per day</strong></li>
              <li>All endpoints available</li>
              <li>Dedicated rate limits</li>
              <li>Priority support</li>
              <li>Advanced analytics</li>
              <li>Custom integrations</li>
            </ul>
          </div>

          <div class="pricing-card">
            <div class="tier-badge enterprise">ENTERPRISE</div>
            <h3>Enterprise Tier</h3>
            <div class="price">Custom</div>
            <ul class="features">
              <li><strong>Unlimited requests</strong></li>
              <li>All endpoints available</li>
              <li>No rate limiting</li>
              <li>24/7 dedicated support</li>
              <li>Custom SLA</li>
              <li>White-label options</li>
            </ul>
          </div>
        </div>

        <h3>4.1 Rate Limit Headers</h3>
        <p>All API responses include the following rate limit headers:</p>
        <pre class="code-block">X-RateLimit-Limit: 100
X-RateLimit-Remaining: 87
X-RateLimit-Reset: 2025-12-14T00:00:00Z</pre>

        <h3>4.2 Exceeding Rate Limits</h3>
        <p>
          Requests exceeding your tier's daily limit will receive a <code>429 Too Many Requests</code> response. 
          Limits reset daily at midnight UTC. Repeated abuse may result in account suspension.
        </p>
      </section>

      <section id="acceptable-use" class="section">
        <h2>5. Acceptable Use Policy</h2>
        
        <h3>5.1 Permitted Uses</h3>
        <ul>
          <li>Personal investment research and analysis</li>
          <li>Integration into educational platforms</li>
          <li>Building financial tools and applications</li>
          <li>Academic and research purposes</li>
          <li>Portfolio management and tracking</li>
        </ul>

        <h3>5.2 Prohibited Uses</h3>
        <div class="warning-box">
          <strong>🚫 You may NOT use the API for:</strong>
          <ul>
            <li>High-frequency trading or automated trading systems</li>
            <li>Reselling or redistributing API data</li>
            <li>Reverse engineering or copying our AI models</li>
            <li>Scraping, crawling, or excessive automated requests</li>
            <li>Providing financial advice without proper licensing</li>
            <li>Market manipulation or insider trading</li>
            <li>Spam, fraud, or illegal activities</li>
            <li>Overloading or disrupting our infrastructure</li>
          </ul>
        </div>

        <h3>5.3 Compliance</h3>
        <p>
          You are responsible for ensuring your use of the API complies with all applicable laws and regulations, 
          including but not limited to securities laws, data protection laws, and financial services regulations 
          in your jurisdiction.
        </p>
      </section>

      <section id="disclaimers" class="section">
        <h2>6. Investment Disclaimers</h2>
        
        <div class="disclaimer-box">
          <h3>⚠️ NOT FINANCIAL ADVICE</h3>
          <p>
            <strong>The InvestBuddy API provides data and predictions for informational and educational purposes only.</strong>
          </p>
          <ul>
            <li>API responses are NOT financial, investment, tax, or legal advice</li>
            <li>You should NOT make investment decisions based solely on API data</li>
            <li>Always consult with a licensed financial advisor before investing</li>
            <li>Past performance does NOT guarantee future results</li>
            <li>Stock predictions carry inherent uncertainty and risk</li>
          </ul>
        </div>

        <h3>6.1 Model Accuracy</h3>
        <p>
          While our LSTM model achieved 55.41% OOS win rate in walk-forward backtesting (2022-2025, 241 trades on 58 stocks with monthly rebalancing), 
          this does not guarantee future performance. Market conditions change, and no prediction model is 100% accurate.
        </p>

        <h3>6.2 Investment Risks</h3>
        <p>
          All investments carry risk, including the potential loss of principal. You acknowledge that:
        </p>
        <ul>
          <li>Stock prices can fluctuate significantly</li>
          <li>Market conditions can change rapidly</li>
          <li>Predictions may be incorrect or outdated</li>
          <li>You bear full responsibility for your investment decisions</li>
        </ul>
      </section>

      <section id="data-privacy" class="section">
        <h2>7. Data & Privacy</h2>
        
        <h3>7.1 Data Collection</h3>
        <p>When you use the API, we collect and log:</p>
        <ul>
          <li>API endpoint accessed</li>
          <li>Request parameters (e.g., stock symbols)</li>
          <li>Response time and status code</li>
          <li>IP address and user agent</li>
          <li>Timestamp of each request</li>
        </ul>

        <h3>7.2 Data Retention</h3>
        <p>
          API usage logs are retained for <strong>90 days</strong> for billing, analytics, and debugging purposes. 
          After 90 days, detailed logs are automatically deleted. Aggregated statistics may be retained indefinitely.
        </p>

        <h3>7.3 Data Security</h3>
        <p>
          We employ industry-standard security measures to protect your data:
        </p>
        <ul>
          <li>API keys are hashed using bcrypt (never stored in plaintext)</li>
          <li>All API traffic is encrypted via HTTPS/TLS</li>
          <li>Database access is restricted and audited</li>
          <li>Regular security audits and penetration testing</li>
        </ul>

        <h3>7.4 GDPR Compliance</h3>
        <p>
          For users in the European Union, you have the right to:
        </p>
        <ul>
          <li>Access your API usage data</li>
          <li>Request deletion of your data</li>
          <li>Export your data in machine-readable format</li>
          <li>Object to automated decision-making</li>
        </ul>
        <p>Contact <a href="mailto:support@investbuddy.ai">support@investbuddy.ai</a> to exercise these rights.</p>
      </section>

      <section id="liability" class="section">
        <h2>8. Limitation of Liability</h2>
        
        <h3>8.1 Service Availability</h3>
        <p>
          The API is provided "as is" without warranties of any kind. We do not guarantee:
        </p>
        <ul>
          <li>Uninterrupted or error-free service</li>
          <li>Accuracy, reliability, or timeliness of data</li>
          <li>Fitness for a particular purpose</li>
          <li>Prevention of unauthorized access</li>
        </ul>

        <h3>8.2 Liability Cap</h3>
        <div class="legal-box">
          <p>
            <strong>TO THE MAXIMUM EXTENT PERMITTED BY LAW:</strong>
          </p>
          <p>
            InvestBuddy and its affiliates shall not be liable for any indirect, incidental, special, consequential, 
            or punitive damages, including but not limited to loss of profits, data, or investment losses, arising 
            from your use of the API.
          </p>
          <p>
            Our total liability to you for all claims shall not exceed the amount you paid us in the 12 months 
            preceding the claim, or $100, whichever is greater.
          </p>
        </div>

        <h3>8.3 Indemnification</h3>
        <p>
          You agree to indemnify and hold harmless InvestBuddy from any claims, damages, or expenses arising from:
        </p>
        <ul>
          <li>Your violation of these Terms</li>
          <li>Your misuse of the API</li>
          <li>Investment decisions based on API data</li>
          <li>Third-party claims related to your use of the API</li>
        </ul>
      </section>

      <section id="termination" class="section">
        <h2>9. Termination</h2>
        
        <h3>9.1 Termination by You</h3>
        <p>
          You may terminate your API access at any time by:
        </p>
        <ul>
          <li>Revoking all API keys in your dashboard</li>
          <li>Canceling your subscription</li>
          <li>Deleting your account</li>
        </ul>

        <h3>9.2 Termination by Us</h3>
        <p>
          We reserve the right to suspend or terminate your API access immediately if you:
        </p>
        <ul>
          <li>Violate these Terms or our Acceptable Use Policy</li>
          <li>Engage in fraudulent or illegal activities</li>
          <li>Fail to pay applicable fees</li>
          <li>Abuse or attempt to exploit the Service</li>
          <li>Pose a security risk to our systems or users</li>
        </ul>

        <h3>9.3 Effect of Termination</h3>
        <p>
          Upon termination:
        </p>
        <ul>
          <li>Your API keys will be immediately revoked</li>
          <li>All API requests will be denied</li>
          <li>Usage data may be retained per our retention policy</li>
          <li>Prepaid fees are non-refundable</li>
        </ul>
      </section>

      <section id="changes" class="section">
        <h2>10. Changes to Terms</h2>
        <p>
          We may update these Terms from time to time. We will notify you of material changes via:
        </p>
        <ul>
          <li>Email to your registered address</li>
          <li>Notification in your dashboard</li>
          <li>Notice on this page</li>
        </ul>
        <p>
          Continued use of the API after changes constitutes acceptance of the new Terms. If you do not agree to 
          the updated Terms, you must stop using the API.
        </p>
      </section>

      <section class="section contact-section">
        <h2>Questions or Concerns?</h2>
        <p>
          If you have questions about these API Terms of Service, please contact us:
        </p>
        <div class="contact-grid">
          <div class="contact-card">
            <div class="icon">📧</div>
            <h4>For All Inquiries</h4>
            <a href="mailto:support@investbuddy.ai">support@investbuddy.ai</a>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted } from 'vue'

onMounted(() => {
  // Smooth scroll to anchor if present in URL
  const hash = window.location.hash
  if (hash) {
    setTimeout(() => {
      const element = document.querySelector(hash)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' })
      }
    }, 100)
  }
})
</script>

<style scoped>
.api-terms-page {
  min-height: 100vh;
  background: #f7fafc;
}

.hero-section {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 4rem 2rem;
  text-align: center;
}

.hero-section h1 {
  font-size: 3rem;
  font-weight: 700;
  margin: 0 0 1rem 0;
}

.subtitle {
  font-size: 1.125rem;
  opacity: 0.9;
}

.container {
  max-width: 900px;
  margin: 0 auto;
  padding: 0 2rem;
}

.content {
  padding: 3rem 2rem;
}

.toc {
  background: white;
  border: 2px solid #667eea;
  border-radius: 12px;
  padding: 2rem;
  margin-bottom: 3rem;
}

.toc h3 {
  margin: 0 0 1rem 0;
  color: #667eea;
  font-size: 1.25rem;
}

.toc ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.toc li {
  margin: 0.75rem 0;
}

.toc a {
  color: #4a5568;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s;
}

.toc a:hover {
  color: #667eea;
}

.section {
  background: white;
  border-radius: 12px;
  padding: 2.5rem;
  margin-bottom: 2rem;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.section h2 {
  color: #2d3748;
  font-size: 2rem;
  margin: 0 0 1.5rem 0;
  padding-bottom: 0.75rem;
  border-bottom: 3px solid #667eea;
}

.section h3 {
  color: #4a5568;
  font-size: 1.5rem;
  margin: 2rem 0 1rem 0;
}

.section h4 {
  color: #4a5568;
  font-size: 1.125rem;
  margin: 1.5rem 0 0.75rem 0;
}

.section p {
  color: #4a5568;
  line-height: 1.8;
  margin: 1rem 0;
}

.section ul, .section ol {
  color: #4a5568;
  line-height: 1.8;
  margin: 1rem 0;
  padding-left: 1.5rem;
}

.section li {
  margin: 0.5rem 0;
}

.section code {
  background: #f7fafc;
  padding: 0.2rem 0.4rem;
  border-radius: 4px;
  font-family: 'Monaco', 'Courier New', monospace;
  font-size: 0.9em;
  color: #667eea;
}

.feature-box, .warning-box, .disclaimer-box, .legal-box {
  padding: 1.5rem;
  border-radius: 8px;
  margin: 1.5rem 0;
}

.feature-box {
  background: #ebf8ff;
  border-left: 4px solid #4299e1;
}

.feature-box h4 {
  margin-top: 0;
  color: #2c5282;
}

.warning-box {
  background: #fef5e7;
  border-left: 4px solid #f39c12;
}

.warning-box strong {
  color: #7d6608;
  display: block;
  margin-bottom: 0.5rem;
}

.disclaimer-box {
  background: #fed7d7;
  border-left: 4px solid #fc8181;
}

.disclaimer-box h3 {
  color: #c53030;
  margin-top: 0;
}

.legal-box {
  background: #f7fafc;
  border: 2px solid #cbd5e0;
}

.legal-box p {
  margin: 0.75rem 0;
}

.pricing-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin: 2rem 0;
}

.pricing-card {
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  transition: all 0.3s;
}

.pricing-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 16px rgba(0,0,0,0.1);
}

.pricing-card.popular {
  border-color: #667eea;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.2);
}

.tier-badge {
  display: inline-block;
  padding: 0.5rem 1rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  margin-bottom: 1rem;
}

.tier-badge.free {
  background: #e2e8f0;
  color: #2d3748;
}

.tier-badge.developer {
  background: #bee3f8;
  color: #2c5282;
}

.tier-badge.business {
  background: #c6f6d5;
  color: #22543d;
}

.tier-badge.enterprise {
  background: #fed7d7;
  color: #742a2a;
}

.pricing-card h3 {
  margin: 0 0 1rem 0;
  color: #2d3748;
  font-size: 1.5rem;
}

.price {
  font-size: 3rem;
  font-weight: 700;
  color: #667eea;
  margin-bottom: 1.5rem;
}

.price span {
  font-size: 1rem;
  color: #718096;
  font-weight: 400;
}

.pricing-card .features {
  list-style: none;
  padding: 0;
  text-align: left;
}

.pricing-card .features li {
  padding: 0.75rem 0;
  border-top: 1px solid #e2e8f0;
}

.code-block {
  background: #1a202c;
  color: #48bb78;
  padding: 1.5rem;
  border-radius: 8px;
  overflow-x: auto;
  font-family: 'Monaco', 'Courier New', monospace;
  font-size: 0.875rem;
  margin: 1rem 0;
}

.contact-section {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.contact-section h2 {
  color: white;
  border-bottom-color: rgba(255, 255, 255, 0.3);
}

.contact-section p {
  color: rgba(255, 255, 255, 0.9);
}

.contact-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
}

.contact-card {
  background: rgba(255, 255, 255, 0.1);
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  transition: all 0.3s;
}

.contact-card:hover {
  background: rgba(255, 255, 255, 0.15);
  transform: translateY(-4px);
}

.contact-card .icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.contact-card h4 {
  margin: 0 0 0.5rem 0;
  color: white;
}

.contact-card a {
  color: white;
  text-decoration: none;
  font-weight: 600;
  border-bottom: 1px solid rgba(255, 255, 255, 0.3);
}

.contact-card a:hover {
  border-bottom-color: white;
}

@media (max-width: 768px) {
  .hero-section h1 {
    font-size: 2rem;
  }

  .section {
    padding: 1.5rem;
  }

  .pricing-grid {
    grid-template-columns: 1fr;
  }
}
</style>
